import { useEffect, useState } from "react";

import { AuthContext } from "../../../auth/AuthProvider";
import { churchSupabase } from "../services/churchSupabaseClient";
import {
  clearChurchOrgUserSession,
  createChurchOrgAuthUser,
  createChurchOrgProfile,
  getStoredChurchOrgUserSession,
  refreshStoredChurchOrgUserSession,
} from "../services/churchOrgUserAuthService";

const AUTH_DEBUG_PREFIX = "[ChurchAuth]";
const CHURCH_AUTH_TIMEOUT_MS = 10000;

function shouldLogAuthDebug() {
  if (typeof window === "undefined") return process.env.NODE_ENV !== "production";

  return (
    process.env.NODE_ENV !== "production" ||
    window.localStorage.getItem("oikos.church.debugAuth") === "true"
  );
}

function describeUser(nextUser) {
  if (!nextUser) return { present: false };

  return {
    present: true,
    id: String(nextUser.id || "").slice(0, 8),
    email: nextUser.email || "",
    isChurchOrgUser: Boolean(nextUser.isChurchOrgUser),
  };
}

function createFallbackProfile(nextUser) {
  if (!nextUser?.id) return null;

  return {
    id: nextUser.id,
    email: nextUser.email || "",
    full_name:
      nextUser.user_metadata?.full_name ||
      nextUser.user_metadata?.name ||
      nextUser.email ||
      "",
    is_approved: true,
    metadata: nextUser.user_metadata || {},
  };
}

function authDebug(message, details = {}) {
  if (!shouldLogAuthDebug()) return;
  console.info(AUTH_DEBUG_PREFIX, message, details);
}

function withTimeout(promise, label) {
  return Promise.race([
    promise,
    new Promise((_, reject) => {
      setTimeout(() => reject(new Error(`${label} timed out`)), CHURCH_AUTH_TIMEOUT_MS);
    }),
  ]);
}

export default function ChurchAuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  async function refreshProfile(nextUser = user) {
    const nextProfile = nextUser?.isChurchOrgUser
      ? createChurchOrgProfile(nextUser.churchOrgSession)
      : createFallbackProfile(nextUser);

    setProfile(nextProfile);
    return nextProfile;
  }

  useEffect(() => {
    let mounted = true;

    async function useStoredOrgUserSession() {
      try {
        authDebug("org session restore started", {
          hasStoredSession: Boolean(getStoredChurchOrgUserSession()),
        });
        const orgSession = await withTimeout(
          refreshStoredChurchOrgUserSession(),
          "Church org session restore"
        );

        authDebug("org session restore result", {
          mounted,
          found: Boolean(orgSession),
          accountId: String(orgSession?.account?.id || "").slice(0, 8),
          orgUserId: String(orgSession?.orgUser?.id || "").slice(0, 8),
          expiresAt: orgSession?.expiresAt || "",
        });

        if (!mounted || !orgSession) return false;

        const orgAuthUser = createChurchOrgAuthUser(orgSession);
        setUser(orgAuthUser);
        setProfile(createChurchOrgProfile(orgSession));
        setLoading(false);
        authDebug("org session restored into provider", {
          user: describeUser(orgAuthUser),
          loading: false,
        });
        return true;
      } catch (error) {
        if (mounted) {
          console.error("Church org user session load error:", error);
          authDebug("org session restore failed", {
            message: error?.message || String(error),
          });
        }
        return false;
      }
    }

    async function restoreInitialSession() {
      if (!mounted) return;
      authDebug("initial session restore started");

      const restored = await useStoredOrgUserSession();
      if (!mounted) {
        authDebug("initial session restore abandoned after org restore: unmounted");
        return;
      }

      if (restored) {
        authDebug("initial session restore finished from org session");
        return;
      }

      authDebug("Supabase getSession started");
      const { data, error } = await withTimeout(
        churchSupabase.auth.getSession(),
        "Church Supabase session restore"
      );
      if (!mounted) return;

      if (error) {
        console.error("Church auth load error:", error);
        authDebug("Supabase getSession failed", {
          message: error?.message || String(error),
        });
        setUser(null);
        setProfile(null);
        setLoading(false);
        return;
      }

      const currentUser = data?.session?.user || null;
      authDebug("Supabase getSession finished", {
        hasSession: Boolean(data?.session),
        user: describeUser(currentUser),
      });

      setUser(currentUser);
      setProfile(createFallbackProfile(currentUser));
      setLoading(false);
      authDebug("initial session restore finished from Supabase");
    }

    restoreInitialSession().catch((error) => {
      if (!mounted) return;
      console.error("Church auth load error:", error);
      authDebug("initial session restore failed", {
        message: error?.message || String(error),
      });
      setUser(null);
      setProfile(null);
      setLoading(false);
      authDebug("initial session restore cleared state after failure");
    });

    const { data: listener } = churchSupabase.auth.onAuthStateChange(async (event, session) => {
      if (!mounted) return;
      authDebug("auth state event received", {
        event,
        user: describeUser(session?.user || null),
      });

      if (event === "INITIAL_SESSION") {
        return;
      }

      const nextUser = session?.user || null;
      const hasStoredOrgSession = Boolean(getStoredChurchOrgUserSession());
      authDebug("auth state event handling", {
        event,
        nextUser: describeUser(nextUser),
        hasStoredOrgSession,
      });

      if (nextUser && event === "SIGNED_IN") {
        clearChurchOrgUserSession();
        authDebug("Supabase sign-in cleared org session");
      } else if (hasStoredOrgSession && !nextUser) {
        const restored = await useStoredOrgUserSession();
        if (restored) {
          authDebug("auth state event restored org session", { event });
          return;
        }
      }

      setUser(nextUser);
      setProfile(createFallbackProfile(nextUser));
      setLoading(false);
      authDebug("auth state event finished", {
        event,
        user: describeUser(nextUser),
      });
    });

    return () => {
      mounted = false;
      listener.subscription.unsubscribe();
      authDebug("provider effect cleaned up");
    };
  }, []);

  return (
    <AuthContext.Provider value={{ user, profile, profileReady: true, loading, refreshProfile }}>
      {children}
    </AuthContext.Provider>
  );
}
